'use client';

import { useState } from 'react';
import { Check, ChevronsUpDown, Settings } from 'lucide-react';
import { useOrganization } from '@/lib/OrganizationContext';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface OrganizationSelectorProps {
  className?: string;
  onManageOrganization?: () => void;
}

export function OrganizationSelector({ 
  className,
  onManageOrganization 
}: OrganizationSelectorProps) {
  const [open, setOpen] = useState(false);
  const { 
    currentOrganization, 
    organizations, 
    switchOrganization,
    isLoading 
  } = useOrganization();

  const handleSelect = async (organizationId: string) => {
    if (organizationId !== currentOrganization?.id) {
      await switchOrganization(organizationId);
    }
    setOpen(false);
  };

  if (isLoading) {
    return (
      <div className={cn("w-[200px] animate-pulse", className)}>
        <div className="h-9 bg-muted rounded-md"></div>
      </div>
    );
  }

  return (
    <div className={cn("w-[200px]", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-controls="org-selector-options"
            className="w-full justify-between"
          >
            <div className="flex items-center space-x-2">
              {currentOrganization?.logo_url && (
                <img 
                  src={currentOrganization.logo_url} 
                  alt={currentOrganization.name}
                  width={16}
                  height={16}
                  loading="lazy"
                  className="h-4 w-4 rounded"
                />
              )}
              <span className="truncate">
                {currentOrganization?.name || "Seleccionar organización"}
              </span>
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0">
          <Command>
            <CommandInput placeholder="Buscar organización..." />
            <CommandEmpty>No se encontraron organizaciones.</CommandEmpty>
            <CommandGroup id="org-selector-options">
              {organizations.map((organization) => (
                <CommandItem
                  key={organization.id}
                  value={organization.id}
                  onSelect={() => handleSelect(organization.id)}
                >
                  <div className="flex items-center space-x-2 flex-1">
                    {organization.logo_url && (
                      <img 
                        src={organization.logo_url} 
                        alt={organization.name}
                        width={16}
                        height={16}
                        loading="lazy"
                        className="h-4 w-4 rounded"
                      />
                    )}
                    <span className="truncate">{organization.name}</span>
                  </div>
                  <Check
                    className={cn(
                      "ml-2 h-4 w-4",
                      currentOrganization?.id === organization.id 
                        ? "opacity-100" 
                        : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
            {onManageOrganization && currentOrganization && (
              <>
                <div className="border-t my-1"></div>
                <CommandGroup>
                  <CommandItem onSelect={() => {
                    setOpen(false);
                    onManageOrganization();
                  }}>
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Configuración</span>
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
